import { apiGet, apiPost, apiDelete } from './client'
import type { ApiResponse } from '@crm/shared'

export interface AttachmentRow {
  id: string
  tenant_id: string
  object_id: string
  record_id: string
  file_name: string
  file_size: number
  content_type: string
  r2_key: string
  uploaded_by: string
  uploaded_by_name: string
  created_at: string
}

export async function fetchAttachments(objectId: string, recordId: string) {
  return apiGet<ApiResponse<AttachmentRow[]>>(
    `/objects/${objectId}/records/${recordId}/attachments`,
  )
}

export async function uploadAttachment(
  objectId: string,
  recordId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<ApiResponse<AttachmentRow>> {
  const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'
  const token = (await import('../stores/auth.store')).useAuthStore.getState().token

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE}/objects/${objectId}/records/${recordId}/attachments`)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      })
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          reject(new Error('Invalid response'))
        }
      } else {
        try {
          const err = JSON.parse(xhr.responseText)
          reject(new Error(err.error?.message || 'Upload failed'))
        } catch {
          reject(new Error('Upload failed'))
        }
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error')))
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

    const formData = new FormData()
    formData.append('file', file)
    xhr.send(formData)
  })
}

export async function downloadAttachment(attachmentId: string, fileName: string) {
  const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'
  const token = (await import('../stores/auth.store')).useAuthStore.getState().token

  const response = await fetch(`${API_BASE}/attachments/${attachmentId}/download`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    throw new Error('Download failed')
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function deleteAttachment(attachmentId: string) {
  return apiDelete<ApiResponse<{ success: boolean }>>(`/attachments/${attachmentId}`)
}
